import {useState} from 'react';
import cx from 'classnames';

import {Text} from '#component/base/Text';
import {useOnceEffect} from '#hook';

import {NumberTickerProps} from './NumberTicker.types';

import * as css from './NumberTicker.css';

const isNumber = (string: string) => !isNaN(parseInt(string, 10));
const numberRange = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

export const NumberTicker = ({
  appear = false,
  children,
  className,
  initialValue,
  ...otherProps
}: NumberTickerProps) => {
  const [useInitialValue, setUseInitialValue] = useState(appear);
  const childrenArray = `${children || 0}`.split('');
  const renderedInitialValue = initialValue
    ? `${initialValue}`?.split('')
    : '0'.repeat(childrenArray.length).split('');

  useOnceEffect(() => {
    if (appear) {
      setTimeout(() => {
        setUseInitialValue(false);
      }, 300);
    }
  });

  return (
    <Text as="div" className={cx(css.root, className)} role="text" {...otherProps}>
      {(useInitialValue ? renderedInitialValue : childrenArray).map((value, i) => {
        const parsedValue = isNumber(value) ? parseInt(value, 10) : value;

        return typeof parsedValue === 'string' ? (
          // Avoid transitioning non-numeric content (eg ".", ",", or "–")
          <span className={css.digitsWrapper} key={i}>
            {parsedValue}
          </span>
        ) : (
          // Use the index of the value within the array as the key, so that the
          // DOM is reused when the value itself changes. This allows updates to
          // the inline `transform` style to animate via CSS transitions — it's
          // a change to an existing node, not a newly-mounted one.
          <span className={css.digitsWrapper} key={i}>
            <span className={css.placeholder}>{parsedValue}</span>
            <span
              aria-hidden
              className={css.digit}
              style={{
                transform: `translateY(-${
                  (100 / numberRange.length) * numberRange.indexOf(parsedValue)
                }%)`,
              }}>
              {numberRange.map(digit => (
                // Render column of digits from 0–9, and allow parent element to
                // translate to correct position via CSS. Other digits are
                // hidden via overflow.
                <span key={digit}>{digit}</span>
              ))}
            </span>
          </span>
        );
      })}
    </Text>
  );
};
